import React, { useEffect } from 'react';
import { Box, Container, Heading, Text, VStack, UnorderedList, ListItem, Link, HStack, Divider } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';

export const LEGAL = {
  company: 'Round Rock Laundry LLC',
  brand: 'Smart Laundry Basket',
  address: '900 E Palm Valley Blvd, Ste 1006-1007, Round Rock, TX 78664',
  email: 'info@smartlaundrybasket.ai',
  phone: '(512) 569-5939',
  smsNumber: '(737) 250-8255',
  effective: 'September 30, 2026',
};

function Section({ title, id, children }) {
  return (
    <Box id={id} scrollMarginTop="24px">
      <Heading as="h2" size="md" mb={3} color="gray.800">{title}</Heading>
      <VStack align="stretch" spacing={3} color="gray.700" fontSize="sm" lineHeight="tall">
        {children}
      </VStack>
    </Box>
  );
}

function Bullets({ items }) {
  return (
    <UnorderedList spacing={1} pl={2}>
      {items.map((item) => <ListItem key={item}>{item}</ListItem>)}
    </UnorderedList>
  );
}

function LegalLayout({ title, children }) {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    const el = id && document.getElementById(id);
    if (el) el.scrollIntoView();
  }, []);

  return (
    <Box bg="gray.50" minH="100vh" py={{ base: 8, md: 12 }}>
      <Helmet>
        <title>{`${title} | ${LEGAL.brand}`}</title>
      </Helmet>
      <Container maxW="820px" bg="white" borderRadius="xl" boxShadow="sm" p={{ base: 6, md: 10 }}>
        <VStack align="stretch" spacing={8}>
          <Box>
            <Heading as="h1" size="xl" color="gray.900">{title}</Heading>
            <Text mt={2} fontSize="sm" color="gray.500">
              {LEGAL.brand} is operated by {LEGAL.company}. Effective {LEGAL.effective}.
            </Text>
          </Box>
          {children}
          <Divider />
          <HStack spacing={4} fontSize="sm" color="gray.500" flexWrap="wrap">
            <Link href="/privacy-policy">Privacy Policy</Link>
            <Link href="/terms">Terms of Service</Link>
            <Link href="/terms#sms">SMS Terms</Link>
            <Text>&copy; {new Date().getFullYear()} {LEGAL.company}</Text>
          </HStack>
        </VStack>
      </Container>
    </Box>
  );
}

function ContactBlock() {
  return (
    <Text>
      {LEGAL.company} ({LEGAL.brand}), {LEGAL.address}. Email{' '}
      <Link href={`mailto:${LEGAL.email}`} color="blue.600">{LEGAL.email}</Link> or call {LEGAL.phone}.
    </Text>
  );
}

export function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <Section title="Who we are">
        <Text>
          {LEGAL.brand} is software that local laundromats ("Laundries") use to take pickup, delivery and
          in-store laundry orders. When you order from a Laundry through our website, app or text messages,
          the Laundry provides the laundry service and we process your information to run the ordering,
          payment, notification and delivery tools on its behalf.
        </Text>
      </Section>

      <Section title="Information we collect">
        <Bullets items={[
          'Contact details: name, mobile phone number, email address.',
          'Pickup and delivery addresses and instructions.',
          'Order details: services, weights, item counts, preferences, photos of items taken during processing, and order history.',
          'Payment information: processed by our payment processor (Stripe). We do not store full card numbers.',
          'Messages you send us, including text messages and chat conversations.',
          'Driver location during active pickups and deliveries, and basic device and usage data (browser type, pages visited).',
        ]} />
      </Section>

      <Section title="How we use information">
        <Bullets items={[
          'To create and manage your account and verify your phone number with one-time codes.',
          'To schedule, process, price, deliver and support your orders.',
          'To send order updates, pickup and delivery notices, receipts and payment links.',
          'To let you place, change or cancel orders by text message.',
          'To send occasional offers from your Laundry, only if you agreed to receive them.',
          'To prevent fraud, keep the service secure and meet legal obligations.',
        ]} />
      </Section>

      <Section title="Text messaging (SMS)" id="sms">
        <Text>
          If you give us your mobile number and agree to receive texts, or text our number ({LEGAL.smsNumber}),
          we use your number to send the messages described in our <Link href="/terms#sms" color="blue.600">SMS Terms</Link>.
          You can opt out at any time by replying STOP.
        </Text>
        <Text>
          Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP to opt out.
        </Text>
        <Text fontWeight="600">
          No mobile information will be shared with third parties or affiliates for marketing or promotional
          purposes. Text messaging originator opt-in data and consent are excluded from all sharing described in
          this policy and will not be shared with any third parties.
        </Text>
      </Section>

      <Section title="How we share information">
        <Text>We share information only as needed to provide the service:</Text>
        <Bullets items={[
          'With the Laundry you order from, which handles your laundry.',
          'With service providers that work for us, such as payment processing (Stripe), text messaging (Twilio), hosting, mapping and address lookup, email delivery, and AI tools used for customer support chat and item counting.',
          'With delivery partners (such as Uber or DoorDash) when your Laundry uses them for a pickup or delivery, limited to your name, address, phone number and delivery instructions.',
          'When required by law, or to protect the rights and safety of our users, Laundries or the public.',
        ]} />
        <Text>We do not sell your personal information.</Text>
      </Section>

      <Section title="Retention and security">
        <Text>
          We keep information for as long as your account is active or as needed to provide the service, resolve
          disputes and meet legal and accounting requirements. We use encryption in transit, access controls and
          other safeguards to protect your information.
        </Text>
      </Section>

      <Section title="Your choices">
        <Bullets items={[
          'Update your name, email, addresses and notification settings in your account.',
          'Reply STOP to any text to stop text messages; reply START to resume.',
          'Ask us to access, correct or delete your information by contacting us below.',
        ]} />
      </Section>

      <Section title="Children">
        <Text>The service is not intended for children under 13, and we do not knowingly collect their information.</Text>
      </Section>

      <Section title="Changes">
        <Text>We may update this policy. We will post the new version here and update the effective date.</Text>
      </Section>

      <Section title="Contact us">
        <ContactBlock />
      </Section>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service">
      <Section title="About these terms">
        <Text>
          These terms apply when you use {LEGAL.brand} websites, apps and text messaging to order laundry services
          from a participating laundromat ("Laundry"). {LEGAL.brand} is operated by {LEGAL.company}. By using the
          service you agree to these terms and our <Link href="/privacy-policy" color="blue.600">Privacy Policy</Link>.
        </Text>
      </Section>

      <Section title="Accounts">
        <Text>
          You need a valid mobile phone number to create an account. You are responsible for the accuracy of the
          information you provide and for activity on your account.
        </Text>
      </Section>

      <Section title="Orders, pricing and payment">
        <Bullets items={[
          'Each Laundry sets its own services, prices, fees, minimums and service area.',
          'Final prices for weight-based services are set after your laundry is weighed.',
          'Some orders require payment before delivery. We will send you a payment link when your order is ready.',
          'Payments are processed by Stripe on behalf of the Laundry.',
        ]} />
      </Section>

      <Section title="Pickup and delivery">
        <Text>
          Pickup and delivery windows are estimates. Please make sure someone is available or that your laundry is
          left in the agreed location. Laundries may charge a fee for missed pickups or failed deliveries, as shown
          in their policies.
        </Text>
      </Section>

      <Section title="Care of your items">
        <Text>
          The Laundry is responsible for cleaning and handling your items under its own care and claims policies.
          Please report missing or damaged items to the Laundry promptly.
        </Text>
      </Section>

      <Section title="SMS Terms" id="sms">
        <Text>
          <b>Program:</b> {LEGAL.brand} order and account text messages, sent from {LEGAL.smsNumber} and other
          numbers used by participating Laundries.
        </Text>
        <Text>
          <b>What you will receive:</b> one-time login codes; order confirmations, status updates, pickup and
          delivery notices, receipts and payment links; replies when you text us to schedule, change or cancel a
          pickup; and, only if you agreed to them, occasional offers from your Laundry.
        </Text>
        <Text>
          <b>How you opt in:</b> by entering your mobile number and checking the text message consent box when you
          create an account or place an order, or by texting PICKUP or START to our number.
        </Text>
        <Bullets items={[
          'Message frequency varies with your orders and activity.',
          'Message and data rates may apply.',
          'Reply STOP to cancel text messages at any time. You will receive one confirmation text and no further messages unless you reply START.',
          'Reply HELP for help, or contact us using the details below.',
          'Carriers are not liable for delayed or undelivered messages.',
          'Consent to receive marketing texts is not a condition of any purchase.',
        ]} />
        <Text>
          Mobile numbers and text messaging consent are never shared with third parties for marketing. See
          our <Link href="/privacy-policy#sms" color="blue.600">Privacy Policy</Link>.
        </Text>
      </Section>

      <Section title="Acceptable use">
        <Text>
          Do not misuse the service, place fraudulent orders, harass drivers or staff, or attempt to access other
          accounts or our systems without permission.
        </Text>
      </Section>

      <Section title="Disclaimers and limitation of liability">
        <Text>
          The service is provided "as is." To the extent allowed by law, {LEGAL.company} is not liable for indirect
          or consequential damages, and our total liability for any claim related to the service is limited to the
          amount you paid for the order involved.
        </Text>
      </Section>

      <Section title="Changes and governing law">
        <Text>
          We may update these terms by posting a new version here. These terms are governed by the laws of the
          State of Texas.
        </Text>
      </Section>

      <Section title="Contact us">
        <ContactBlock />
      </Section>
    </LegalLayout>
  );
}
